import React from 'react'
import { Flex, Text } from '@fundamentals'
import { DropdownButton, IconButton } from '@common/material'
import {
  ArchiveOutlined,
  EditOutlined,
  MoreHoriz,
  UnarchiveOutlined,
} from '@mui/icons-material'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { RegisterItemAttachment } from '@core/entities/register/RegisterItemAttachment'
import {
  useArchiveRegisterItemAttachment,
  useRestoreRegisterItemAttachment,
} from '@core/react-query/features/registers/registers/register/items/item'
import { RenameRegisterItemAttachmentModal } from '@modules/registers/modals/RenameRegisterItemAttachmentModal'
import ImageModal from '@common/modal/ImageModal'
import { PdfModal } from '@common/PdfModal'
import { VideoModal } from '@common/VideoModal'
import { SiteBoxDocumentViewerModal } from '@modules/siteBoxDocumentSidebar'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import multiDownload from 'multi-download'
import moment from 'moment'
import { FileTypeIcon } from '@modules/siteBox/components/FileTypeIcon'

type Props = {
  attachment: RegisterItemAttachment
  attachments: Array<RegisterItemAttachment>
  readOnly: boolean
  registerId: number
  registerItemId: number
  organisationId: number
  projectId?: number
  isArchived?: boolean
}

export const RegisterItemAttachmentRow: React.FC<Props> = ({
  attachment,
  attachments,
  readOnly,
  isArchived,
  registerId,
  registerItemId,
  organisationId,
  projectId,
}) => {
  const { showModal } = useModal()
  const { flags } = useFeatureFlags(['web_site_box_rams_appraisals'])
  const toast = useToast()
  const archiveAttachment = useArchiveRegisterItemAttachment()
  const restoreAttachment = useRestoreRegisterItemAttachment()
  const fileName = attachment.getDocumentName()
  const url = attachment.getUrl()
  const formattedDate = moment(attachment.createdOn).format('DD/MM/YYYY H:mm')

  const handleOpenAttachment = () => {
    if (!url) return

    const version = attachment.siteBoxDocument?.currentVersion
    const previewMode = version?.getPreviewMode() ?? 'PDF'

    if (previewMode === 'DOWNLOAD') {
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }

    if (previewMode === 'VIDEO') {
      showModal(
        <VideoModal
          title={localizedStrings.video}
          previewUrl={url}
          downloadUrl={url}
        />,
      )
      return
    }

    if (previewMode === 'IMAGE') {
      const imageAttachments = attachments
        .filter((a) => a.isImageFile())
        .map((a) => ({
          downloadUrl: a.getUrl(),
          key: a.siteBoxDocument?.currentVersion?.documentKey,
        }))

      showModal(
        <ImageModal
          title={localizedStrings.image}
          previewUrl={url}
          images={imageAttachments}
          s3Key={attachment.siteBoxDocument?.currentVersion?.documentKey}
          canDownload
          onDownloadAll={() => {
            const downloadUrls = imageAttachments
              .map((a) => a.downloadUrl)
              .filter((downloadUrl): downloadUrl is string => !!downloadUrl)
            if (downloadUrls.length > 0) {
              multiDownload(downloadUrls)
            }
          }}
        />,
      )
      return
    }

    const documentAttachments = attachments.filter(
      (a) => a.getFileFormat() === 'PDF',
    )
    const shouldUseSiteBoxViewer =
      flags.web_site_box_rams_appraisals &&
      !!projectId &&
      !!attachment.siteBoxDocument

    if (shouldUseSiteBoxViewer) {
      showModal(
        <SiteBoxDocumentViewerModal
          organisationId={organisationId}
          projectId={projectId}
          documents={documentAttachments.map((a) => ({
            id: a.siteBoxDocument?.id ?? a.id,
            name: a.getDocumentName(),
            siteBoxDocument: a.siteBoxDocument,
            documentUrl: a.getUrl(),
          }))}
          initialItemId={attachment.siteBoxDocument?.id ?? attachment.id}
          readOnly
        />,
      )
      return
    }

    showModal(
      <PdfModal
        readOnly
        title={localizedStrings.pdf}
        previewUrl={url}
        documents={documentAttachments.map((a) => ({
          getUrl: a.getUrl(),
          id: a.siteBoxDocument?.id,
          siteBoxDocumentVersionId: a.siteBoxDocument?.currentVersion?.id,
        }))}
        canDownload
        onDownloadAll={() => {
          const downloadUrls = documentAttachments
            .map((a) => a.getUrl())
            .filter((downloadUrl): downloadUrl is string => !!downloadUrl)
          if (downloadUrls.length > 0) {
            multiDownload(downloadUrls)
          }
        }}
      />,
    )
  }

  const options = !isArchived
    ? [
        ...(attachment.source === 'UPLOAD'
          ? [
              {
                title: localizedStrings.rename,
                value: 'rename',
                icon: <EditOutlined />,
                onSelect: () =>
                  showModal(
                    <RenameRegisterItemAttachmentModal
                      registerId={registerId}
                      registerItemId={registerItemId}
                      attachmentId={attachment.id}
                      initialName={fileName}
                    />,
                  ),
              },
            ]
          : []),
        {
          title: localizedStrings.archive,
          value: 'archive',
          color: 'error.main',
          icon: <ArchiveOutlined />,
          onSelect: () =>
            archiveAttachment.mutate(
              {
                registerId,
                registerItemId,
                attachmentId: attachment.id,
              },
              {
                onSuccess: () => toast.success(localizedStrings.fileArchived),
                onError: (e) => toast.error(e.message),
              },
            ),
        },
      ]
    : [
        {
          title: localizedStrings.unarchive,
          value: 'unarchive',
          icon: <UnarchiveOutlined />,
          onSelect: () =>
            restoreAttachment.mutate(
              {
                registerId,
                registerItemId,
                attachmentId: attachment.id,
              },
              {
                onSuccess: () => toast.success(localizedStrings.fileRestored),
                onError: (e) => toast.error(e.message),
              },
            ),
        },
      ]

  return (
    <Flex
      justifyContent='space-between'
      alignItems='center'
      py={0.75}
      mb={1}
      sx={{
        borderRadius: 2,
        p: 1,
        '&:hover': {
          bgcolor: 'action.hover',
          '& .icon-button': { opacity: 1 },
        },
      }}
    >
      <Flex
        alignItems='flex-start'
        gap={1}
        sx={{ cursor: url ? 'pointer' : 'default', flex: 1, minWidth: 0 }}
        onClick={() => url && handleOpenAttachment()}
      >
        <FileTypeIcon
          format={attachment.getFileFormat()}
          sx={{ color: 'text.disabled', width: 20, height: 20 }}
        />
        <Flex flexDirection='column' gap={0}>
          <Text variant='body2'>{fileName}</Text>
          <Text variant='caption' color='text.secondary'>
            {`${formattedDate} ${attachment.getUserName()}`}
          </Text>
        </Flex>
      </Flex>
      {!readOnly && (
        <DropdownButton
          label={localizedStrings.new}
          options={options}
          renderButton={(onClick) => (
            <IconButton
              className='icon-button'
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                onClick(e)
              }}
              sx={{
                opacity: 0,
                transition: 'opacity 0.25s',
                color: 'text.secondary',
              }}
            >
              <MoreHoriz fontSize='small' />
            </IconButton>
          )}
        />
      )}
    </Flex>
  )
}
